import { useEffect, useState } from 'react';
import { useT } from '@/store';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { PAGE_SIZES } from '@/hooks/usePagedList';

/**
 * The search box that sits in a Panel's `actions` slot.
 *
 * It owns the text the dispatcher is typing and hands the parent only the
 * debounced value, so a parent re-render per keystroke never happens and the
 * input never loses characters to a slow refetch. `onDebouncedChange` is
 * called with the settled term; the caller puts that in its query key.
 */
export function SearchInput({ value, onDebouncedChange, placeholder, delay = 300 }) {
  const t = useT();
  const [typed, setTyped] = useState(value ?? '');
  const settled = useDebouncedValue(typed, delay);

  useEffect(() => {
    onDebouncedChange(settled);
    // onDebouncedChange is a setState from the parent and is stable enough
    // in practice; including it would re-fire this on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  return (
    <div className="relative">
      <input
        type="search"
        className="field !w-[180px] !py-1 !pl-7 !text-[11px]"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={placeholder ?? t('common.searchPh')}
        aria-label={placeholder ?? t('common.searchPh')}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-dim"
      >
        ⌕
      </span>
    </div>
  );
}

/**
 * "26–50 iš 412", a page size selector, and the two arrows.
 *
 * Renders nothing at all when everything fits on one page — a footer saying
 * "1–6 of 6" next to two dead arrows is furniture, and most tenants' quarry
 * lists will never outgrow a single page.
 *
 * `total` comes from the API's total_count rather than from `data.length`,
 * which is the whole reason the endpoints answer with an envelope: the page
 * knows how many rows it holds, and only the server knows how many there
 * are.
 */
export function PaginationFooter({ total, limit, offset, onOffsetChange, onLimitChange }) {
  const t = useT();
  // The CALLER's current page size, not PAGE_SIZES[0]. A list picked up to
  // 100-per-page shows every one of 40 rows on a single page — the footer
  // must hide then too, not just when the (unrelated) smallest option would
  // also have fit them all.
  if (total <= limit && offset === 0) return null;

  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + limit, total);
  const canGoBack = offset > 0;
  const canGoForward = offset + limit < total;

  return (
    <footer className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
      <span className="text-[11px] text-dim">
        {t('common.pageRange', { first, last, total })}
      </span>

      <div className="flex items-center gap-1.5">
        <select
          className="field !w-auto !py-1 !text-[11px]"
          value={limit}
          onChange={(e) => {
            // Back to the first page on a size change. Staying at offset 200
            // while switching to 100-per-page shows a different slice than
            // the one the dispatcher was looking at, which reads as the
            // table having jumped on its own.
            onLimitChange(Number(e.target.value));
            onOffsetChange(0);
          }}
          aria-label={t('common.perPage')}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {t('common.perPageN', { n: size })}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="btn !px-2.5 !py-1 !text-[11px]"
          disabled={!canGoBack}
          onClick={() => onOffsetChange(Math.max(0, offset - limit))}
        >
          ‹ {t('common.prevPage')}
        </button>
        <button
          type="button"
          className="btn !px-2.5 !py-1 !text-[11px]"
          disabled={!canGoForward}
          onClick={() => onOffsetChange(offset + limit)}
        >
          {t('common.nextPage')} ›
        </button>
      </div>
    </footer>
  );
}
